import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, CheckCircle, Clock, AlertCircle, Heart, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ReviewSubmit = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Review form state
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewerName, setReviewerName] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [projectType, setProjectType] = useState('');
  const [wouldRecommend, setWouldRecommend] = useState(true);
  const [reviewLinks, setReviewLinks] = useState(null);

  useEffect(() => {
    fetchFormData();
  }, [token]);

  const fetchFormData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/reviews/form/${token}`);
      const data = response.data;
      
      if (data.status === 'submitted') {
        setStatus('submitted');
      } else if (data.status === 'expired') {
        setStatus('expired');
      } else {
        setStatus('active');
        setFormData(data);
        setReviewLinks(data.review_links || null);
        setReviewerName(data.customer_name || '');
        if (data.job_info?.project_type) {
          setProjectType(data.job_info.project_type);
        }
      }
    } catch (error) {
      console.error('Error fetching form data:', error);
      setStatus('invalid');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!content.trim()) {
      alert('Please share your experience with us');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/api/reviews/submit/${token}`, {
        reviewer_name: reviewerName,
        rating: rating,
        title: title || null,
        content: content,
        project_type: projectType || null,
        would_recommend: wouldRecommend
      });
      
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting review:', error);
      if (error.response?.data?.detail) {
        alert(error.response.data.detail);
      } else {
        alert('Failed to submit review. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a1a1a] via-[#2d2d2d] to-[#1a1a1a] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Status screens
  if (status === 'invalid') {
    return (
      <StatusScreen
        icon={AlertCircle}
        iconColor="text-red-400"
        title="Invalid Link"
        message="This review link is not valid. Please check the link or contact us for a new one."
      />
    );
  }

  if (status === 'expired') {
    return (
      <StatusScreen
        icon={Clock}
        iconColor="text-orange-400"
        title="Link Expired"
        message="This review link has expired. Please contact us if you'd still like to share your feedback."
      />
    );
  }

  if (status === 'submitted' || submitted) {
    const hasExternalLinks = reviewLinks && (reviewLinks.google_review_url || reviewLinks.yelp_review_url);
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a1a1a] via-[#2d2d2d] to-[#1a1a1a] flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 p-12 text-center max-w-md"
          data-testid="review-thank-you"
        >
          <CheckCircle className="w-16 h-16 mx-auto mb-6 text-green-400" />
          <h2 className="text-2xl font-serif text-white mb-4">Thank You!</h2>
          <p className="text-gray-400 mb-8">
            Your review has been submitted successfully. We truly appreciate your feedback!
          </p>

          {hasExternalLinks && (
            <div className="mb-8">
              <p className="text-gray-300 text-sm mb-4">
                Would you mind sharing your experience publicly? It means the world to us.
              </p>
              <div className="flex flex-col gap-3">
                {reviewLinks.google_review_url && (
                  <a
                    href={reviewLinks.google_review_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-white text-gray-800 px-6 py-3 font-semibold text-sm hover:bg-gray-100 transition-colors"
                    data-testid="review-google-btn"
                  >
                    <Star className="w-4 h-4 text-[#C6A87C]" />
                    Review us on Google
                  </a>
                )}
                {reviewLinks.yelp_review_url && (
                  <a
                    href={reviewLinks.yelp_review_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-[#d32323] text-white px-6 py-3 font-semibold text-sm hover:bg-[#b81f1f] transition-colors"
                    data-testid="review-yelp-btn"
                  >
                    <Star className="w-4 h-4" />
                    Review us on Yelp
                  </a>
                )}
              </div>
            </div>
          )}

          <Link
            to="/"
            className="inline-block bg-[#C6A87C] text-white px-8 py-3 font-semibold text-sm tracking-widest uppercase hover:bg-[#B09265] transition-colors"
          >
            Visit Our Website
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a1a1a] via-[#2d2d2d] to-[#1a1a1a]">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Website
          </Link>
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl font-serif text-white">Jax Remodeling Solutions</h1>
            <p className="text-[#C6A87C] tracking-[0.3em] text-xs mt-2">SHARE YOUR EXPERIENCE</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 md:p-12"
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl font-serif text-white mb-2">
              Hi {formData?.customer_name || 'there'}!
            </h2>
            <p className="text-gray-400">
              {formData?.job_info?.project_type 
                ? `Thank you for choosing us for your ${formData.job_info.project_type.toLowerCase()} project.`
                : 'Thank you for choosing Jax Remodeling Solutions.'
              }
            </p>
            <p className="text-gray-400 mt-1">We'd love to hear about your experience.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Star Rating */}
            <div className="text-center">
              <label className="block text-white text-sm font-medium mb-4">How would you rate your experience?</label>
              <div className="flex justify-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-10 h-10 transition-colors ${
                        star <= (hoverRating || rating)
                          ? 'fill-[#C6A87C] text-[#C6A87C]'
                          : 'text-gray-600'
                      }`}
                    />
                  </button>
                ))}
              </div>
              <p className="text-[#C6A87C] mt-2 text-sm">
                {rating === 5 ? 'Excellent!' : rating === 4 ? 'Great!' : rating === 3 ? 'Good' : rating === 2 ? 'Fair' : 'Poor'}
              </p>
            </div>

            {/* Name */}
            <div>
              <label className="block text-white text-sm font-medium mb-2">Your Name</label>
              <input
                type="text"
                value={reviewerName}
                onChange={(e) => setReviewerName(e.target.value)}
                required
                className="w-full bg-white/5 border border-white/20 text-white px-4 py-3 focus:border-[#C6A87C] focus:outline-none transition-colors"
                placeholder="Your name"
              />
            </div>

            {/* Title (Optional) */}
            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Review Title <span className="text-gray-500">(optional)</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full bg-white/5 border border-white/20 text-white px-4 py-3 focus:border-[#C6A87C] focus:outline-none transition-colors"
                placeholder="e.g., Amazing kitchen transformation!"
              />
            </div>

            {/* Review Content */}
            <div>
              <label className="block text-white text-sm font-medium mb-2">Your Review</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                required
                rows={5}
                className="w-full bg-white/5 border border-white/20 text-white px-4 py-3 focus:border-[#C6A87C] focus:outline-none transition-colors resize-none"
                placeholder="Tell us about your experience working with our team..."
              />
              <p className="text-gray-500 text-xs mt-1">{content.length} characters</p>
            </div>

            {/* Project Type */}
            <div>
              <label className="block text-white text-sm font-medium mb-2">Type of Project</label>
              <select
                value={projectType}
                onChange={(e) => setProjectType(e.target.value)}
                className="w-full bg-white/5 border border-white/20 text-white px-4 py-3 focus:border-[#C6A87C] focus:outline-none transition-colors"
              >
                <option value="">Select project type...</option>
                <option value="Kitchen Remodel">Kitchen Remodel</option>
                <option value="Bathroom Remodel">Bathroom Remodel</option>
                <option value="Full Home Remodel">Full Home Remodel</option>
                <option value="Addition">Addition</option>
                <option value="Flooring">Flooring</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Would Recommend */}
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={wouldRecommend}
                  onChange={(e) => setWouldRecommend(e.target.checked)}
                  className="w-5 h-5 rounded border-white/20 bg-white/5 text-[#C6A87C] focus:ring-[#C6A87C] focus:ring-offset-0"
                />
                <span className="text-white flex items-center gap-2">
                  <Heart className={`w-4 h-4 ${wouldRecommend ? 'text-red-400 fill-red-400' : 'text-gray-400'}`} />
                  I would recommend Jax Remodeling Solutions
                </span>
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#C6A87C] text-white py-4 font-semibold text-sm tracking-widest uppercase hover:bg-[#B09265] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Review'
              )}
            </button>

            <p className="text-gray-500 text-xs text-center">
              By submitting, you agree that your review may be displayed on our website.
            </p>
          </form>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} Jax Remodeling Solutions LLC. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

// Status Screen Component
const StatusScreen = ({ icon: Icon, iconColor, title, message, showHomeLink }) => (
  <div className="min-h-screen bg-gradient-to-br from-[#1a1a1a] via-[#2d2d2d] to-[#1a1a1a] flex items-center justify-center p-6">
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-white/5 backdrop-blur-sm border border-white/10 p-12 text-center max-w-md"
    >
      <Icon className={`w-16 h-16 mx-auto mb-6 ${iconColor}`} />
      <h2 className="text-2xl font-serif text-white mb-4">{title}</h2>
      <p className="text-gray-400 mb-8">{message}</p>
      {showHomeLink && (
        <Link
          to="/"
          className="inline-block bg-[#C6A87C] text-white px-8 py-3 font-semibold text-sm tracking-widest uppercase hover:bg-[#B09265] transition-colors"
        >
          Visit Our Website
        </Link>
      )}
    </motion.div>
  </div>
);

export default ReviewSubmit;
